import is from '@sindresorhus/is';
import { type QueryClient, type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { companyHomeKeys } from '../../company-home/api/query-keys';
import { companySettingsKeys } from '../../company-settings';
import {
    type ContextRefreshEnvelope,
    type EntityIdentifier,
    LEARNING_OPPORTUNITY_ENTITY_TYPE,
} from '../../data-contract/chat';
import { jobArchitectureKeys } from '../../job-architecture-v2/api/query-keys';
import { learningOpportunityKeys } from '../../learning-opportunities-v2/api/query-keys';
// Deep import: the position-alignment barrel loads its pages and tiles, which import the chat barrel.
import { refreshAlignmentCaches } from '../../position-alignment/api/refresh-alignment-caches';
import {
    invalidatePositionCaches,
    invalidatePositionDerivedQueries,
    invalidatePositionListCaches,
    invalidateUnalignedQueue,
} from '../../position-management/api/invalidate-position-caches';
import { positionManagementKeys } from '../../position-management/api/query-keys';

// A leveling framework's translations are filled in by a background job that finishes well after
// the agent's turn ends, and nothing pushes a completion event once the run's stream closes. The
// invalidation below therefore re-reads content that is still being translated, so schedule a few
// follow-up reads across the window that job typically needs.
const TRANSLATION_REFETCH_DELAYS_MS = [10_000, 30_000, 60_000, 120_000];

// Archive/unarchive events carry a single job_architecture entity whose own id is
// the architecture to refresh; item-level events carry it in jobArchitectureId.
const refreshArchitectureId = (entity: EntityIdentifier): string | null | undefined => {
    if (is.nonEmptyString(entity.jobArchitectureId)) return entity.jobArchitectureId;
    return entity.type === 'job_architecture' ? entity.id : null;
};

const isAwaitingTranslation = (entity: EntityIdentifier): boolean => entity.type === 'leveling_framework';

// Tenant-level queries an entity change invalidates, on top of the per-architecture keys handled
// below. Settings entities carry no job architecture and map onto the settings page's queries;
// members and invitations share the user-management prefix, so one key covers both lists and the
// invitation options. A skill re-reads the tenant-wide catalog, which no architecture key covers.
// A learning opportunity re-reads the learning management list, its statistics and open details,
// plus the skill catalog whose rows reference the learning opportunities training a skill.
const tenantQueryKeys = (entity: EntityIdentifier): readonly QueryKey[] => {
    switch (entity.type) {
        case 'company_profile':
            return [companySettingsKeys.companyProfile()];
        case 'company_settings':
            return [companySettingsKeys.companySettings()];
        case 'tenant_member':
        case 'invitation':
            return [companySettingsKeys.userManagement()];
        case 'skill':
            return [jobArchitectureKeys.skillCatalog()];
        // The template queries live under the company-settings keys, not the per-architecture
        // family the generic invalidation below covers, so the settings page only re-reads the
        // template through this mapping.
        case 'job_profile_template':
            return is.nonEmptyString(entity.jobArchitectureId)
                ? [
                      companySettingsKeys.customJobProfileTemplate(entity.jobArchitectureId),
                      companySettingsKeys.managedJobProfileTemplates(),
                  ]
                : [companySettingsKeys.managedJobProfileTemplates()];
        case LEARNING_OPPORTUNITY_ENTITY_TYPE:
            return [
                learningOpportunityKeys.details(),
                learningOpportunityKeys.filter(),
                learningOpportunityKeys.allStatistics(),
                jobArchitectureKeys.skillCatalog(),
            ];
        default:
            return [];
    }
};

const invalidateTenantQueries = (entities: readonly EntityIdentifier[], queryClient: QueryClient): void => {
    for (const entity of entities) {
        for (const queryKey of tenantQueryKeys(entity)) {
            void queryClient.invalidateQueries({ queryKey });
        }
    }
};

// Position events carry no job architecture. A position re-reads its own detail and changelog plus
// the lists and counts that show it; a position architecture re-reads everything under it and the
// bootstrap that names it. An event without an id falls back to the whole family it belongs to.
// An alignment can move its position in or out of the Unaligned queue, so it re-reads its counts and list.
const invalidatePositionQueries = (entity: EntityIdentifier, queryClient: QueryClient): void => {
    if (entity.type === 'alignment') {
        void invalidateUnalignedQueue(queryClient);
        return;
    }
    if (entity.type === 'position') {
        if (is.nonEmptyString(entity.id)) {
            void invalidatePositionCaches(queryClient, { positionId: entity.id });
            return;
        }
        invalidatePositionListCaches(queryClient);
        void queryClient.invalidateQueries({ queryKey: positionManagementKeys.anyPosition() });
        return;
    }
    if (entity.type !== 'position_architecture') return;
    void invalidatePositionDerivedQueries(queryClient);
    if (!is.nonEmptyString(entity.id)) {
        void queryClient.invalidateQueries({ queryKey: positionManagementKeys.all });
        return;
    }
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.bootstrap() });
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.architecture(entity.id) });
};

/** Runs `run` after `delay` milliseconds; the caller owns cancelling what it scheduled. */
export type ScheduleRefetch = (run: () => void, delay: number) => void;

/**
 * Invalidates the architecture, position and tenant-level queries the changed entities touch.
 * Framework entities are re-read a few more times afterwards: their translations are filled in by a
 * background job that outlives the agent's turn, so the first read still returns untranslated content.
 */
export const invalidateContextRefreshQueries = (
    entities: readonly EntityIdentifier[],
    queryClient: QueryClient,
    schedule: ScheduleRefetch,
): void => {
    const invalidate = (architectureId: string): void => {
        void queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.architecture(architectureId) });
    };
    void queryClient.invalidateQueries({ queryKey: companyHomeKeys.journeys() });
    invalidateTenantQueries(entities, queryClient);
    refreshAlignmentCaches(queryClient, entities);
    for (const entity of entities) {
        invalidatePositionQueries(entity, queryClient);
        const architectureId = refreshArchitectureId(entity);
        if (!is.nonEmptyString(architectureId)) continue;
        invalidate(architectureId);
        if (!isAwaitingTranslation(entity)) continue;
        for (const delay of TRANSLATION_REFETCH_DELAYS_MS) {
            schedule(() => invalidate(architectureId), delay);
        }
    }
};

/** Invalidates what an envelope touches once per mount, and cancels its follow-up reads on unmount. */
export const useContextRefreshInvalidation = (envelope: ContextRefreshEnvelope): void => {
    const queryClient = useQueryClient();
    const timeoutsRef = useRef<number[]>([]);
    const hasRunRef = useRef(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: invalidation runs once per envelope mount.
    useEffect(() => {
        if (hasRunRef.current) return;
        hasRunRef.current = true;
        const timeouts = timeoutsRef.current;
        invalidateContextRefreshQueries(envelope.payload.entities, queryClient, (run, delay) => {
            timeouts.push(window.setTimeout(run, delay));
        });
    }, []);

    useEffect(
        () => () => {
            for (const id of timeoutsRef.current) window.clearTimeout(id);
            timeoutsRef.current = [];
        },
        [],
    );
};
